import { AUTH0_FLOW_ENTRY_SESSION_KEY, AUTH0_FLOW_RESTORE_SESSION_KEY } from '@libs/core';

import { mutateSessionStorage, withSessionStorage } from './sessionStorageAccess';

type Auth0InitiatedLoginSearchParams = {
    get(name: string): string | null;
};

const LEGACY_BACK_FORWARD_NAVIGATION_TYPE = 2;
const AUTH0_HISTORY_FALLBACK_PATH = '/';

function getNavigationType(): PerformanceNavigationTiming['type'] | null {
    if (typeof performance === 'undefined') {
        return null;
    }

    try {
        const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (entry?.type) {
            return entry.type;
        }
    } catch {
        // Fall through to the deprecated API for older Safari versions.
    }

    return performance.navigation?.type === LEGACY_BACK_FORWARD_NAVIGATION_TYPE ? 'back_forward' : null;
}

/**
 * Header CTAs persist a storefront origin. Auto-redirect pages (`/sign-in`)
 * omit `flowEntry`, so drop any leftover origin from a previous attempt.
 */
export function rememberAuth0FlowEntry(flowEntry?: string) {
    mutateSessionStorage(storage => {
        if (flowEntry) {
            storage.setItem(AUTH0_FLOW_ENTRY_SESSION_KEY, flowEntry);
        } else {
            storage.removeItem(AUTH0_FLOW_ENTRY_SESSION_KEY);
        }
        storage.removeItem(AUTH0_FLOW_RESTORE_SESSION_KEY);
    });
}

export function clearAuth0FlowNavigation() {
    rememberAuth0FlowEntry();
}

export function readAuth0FlowEntry(): string {
    return withSessionStorage({
        fallback: '',
        read: storage => storage.getItem(AUTH0_FLOW_ENTRY_SESSION_KEY) ?? '',
    });
}

/**
 * Auth0 appends `iss` when it sends a stale/revisited transaction to the
 * application's Login Initiation URI. Only treat that handoff as an aborted
 * flow when the navigation itself came from browser history.
 */
export function isAuth0InitiatedBackNavigation(searchParams?: Auth0InitiatedLoginSearchParams | null): boolean {
    return Boolean(searchParams?.get('iss') && getNavigationType() === 'back_forward');
}

function readAuth0HistorySnapshot(): string {
    return withSessionStorage({
        fallback: AUTH0_HISTORY_FALLBACK_PATH,
        read: storage => {
            const snapshot = storage.getItem(AUTH0_FLOW_RESTORE_SESSION_KEY);
            if (snapshot) {
                return snapshot;
            }

            const destination = storage.getItem(AUTH0_FLOW_ENTRY_SESSION_KEY) || AUTH0_HISTORY_FALLBACK_PATH;
            storage.setItem(AUTH0_FLOW_RESTORE_SESSION_KEY, destination);
            storage.removeItem(AUTH0_FLOW_ENTRY_SESSION_KEY);
            return destination;
        },
    });
}

/**
 * Consume the storefront origin once so a remount (React Strict Mode) cannot
 * fall back to `/` after the flow-entry key was cleared.
 */
export function resolveAuth0HistoryReturnTo(searchParams?: Auth0InitiatedLoginSearchParams | null): string | undefined {
    if (!isAuth0InitiatedBackNavigation(searchParams)) {
        return undefined;
    }

    return readAuth0HistorySnapshot();
}
